import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, 
  Search, 
  Filter, 
  Calendar,
  User,
  MessageSquare,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { adminAPI } from '../services/api';
import { OpenAIError, OpenAIErrorStatistics } from '../types';

const OpenAIErrors: React.FC = () => {
  const [errors, setErrors] = useState<OpenAIError[]>([]);
  const [statistics, setStatistics] = useState<OpenAIErrorStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalErrors, setTotalErrors] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    errorType: '',
    userId: '',
    startDate: '',
    endDate: ''
  });

  const loadErrors = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getOpenAIErrors(
        page, 
        20, 
        filters.errorType, 
        filters.userId, 
        filters.startDate, 
        filters.endDate
      );
      setErrors(data.errors);
      setTotalPages(data.pagination.totalPages);
      setTotalErrors(data.pagination.totalErrors || 0);
    } catch (error) {
      console.error('Error loading OpenAI errors:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const loadStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await adminAPI.getOpenAIErrorStatistics(7);
      setStatistics(data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleSearch = () => {
    loadErrors();
  };

  const handleResetFilters = () => {
    setFilters({
      errorType: '',
      userId: '',
      startDate: '',
      endDate: ''
    });
    setPage(1);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getErrorTypeColor = (errorType: string) => {
    switch (errorType) {
      case 'TIMEOUT_ERROR': return 'bg-yellow-100 text-yellow-800';
      case 'API_ERROR': return 'bg-red-100 text-red-800';
      case 'NETWORK_ERROR': return 'bg-orange-100 text-orange-800';
      case 'RATE_LIMIT_ERROR': return 'bg-purple-100 text-purple-800';
      case 'QUOTA_ERROR': return 'bg-pink-100 text-pink-800';
      case 'AUTH_ERROR': return 'bg-red-100 text-red-800';
      case 'ASSISTANT_ERROR': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const errorTypes = [
    'TIMEOUT_ERROR',
    'API_ERROR', 
    'NETWORK_ERROR',
    'RATE_LIMIT_ERROR',
    'QUOTA_ERROR',
    'AUTH_ERROR',
    'ASSISTANT_ERROR',
    'UNKNOWN_ERROR'
  ];

  if (loading && errors.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Erreurs OpenAI
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Audit des erreurs de connexion avec l'API OpenAI
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => { loadErrors(); loadStatistics(); }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && !statsLoading && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total erreurs
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.summary.totalErrors.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Filter className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Types d'erreurs
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.summary.uniqueErrorTypes}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <User className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Utilisateurs affectés
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.summary.uniqueUsers}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Erreurs récentes
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.topErrors.length > 0 ? statistics.topErrors[0].count : 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtres</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type d'erreur
            </label>
            <select
              value={filters.errorType}
              onChange={(e) => handleFilterChange('errorType', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tous les types</option>
              {errorTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Utilisateur
            </label>
            <input
              type="text"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              placeholder="ID utilisateur"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Search className="h-4 w-4 mr-2" />
              Rechercher
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Errors Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Erreurs ({totalErrors.toLocaleString()})
          </h3>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : errors.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune erreur trouvée</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucune erreur ne correspond aux critères de recherche.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {errors.map((error) => (
              <li key={error.id} className="px-4 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getErrorTypeColor(error.errorType)}`}>
                        {error.errorType}
                      </span>
                      {error.errorCode && (
                        <span className="text-xs text-gray-500">
                          Code: {error.errorCode}
                        </span>
                      )}
                    </div>
                    <div className="mt-1">
                      <p className="text-sm text-gray-900 font-medium">
                        {error.errorMessage}
                      </p>
                      <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(error.createdAt)}
                        </span>
                        {error.userEmail && (
                          <span className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {error.userEmail}
                          </span>
                        )}
                        {error.conversationId && (
                          <span className="flex items-center">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Conversation #{error.conversationId}
                          </span>
                        )}
                        {error.ipAddress && (
                          <span>IP: {error.ipAddress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {error.errorDetails && (
                      <button
                        onClick={() => {
                          const details = JSON.stringify(error.errorDetails, null, 2);
                          alert(`Détails de l'erreur:\n\n${details}`);
                        }}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        Détails
                      </button>
                    )}
                    {error.requestData && (
                      <button
                        onClick={() => {
                          const request = JSON.stringify(error.requestData, null, 2);
                          alert(`Données de la requête:\n\n${request}`);
                        }}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        Requête
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{page}</span> sur{' '}
                  <span className="font-medium">{totalPages}</span> ({totalErrors} erreurs au total)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenAIErrors;
